import {
  Fragment, MouseEvent, ReactNode, useState,
} from 'react';
import { clsx } from 'clsx';
import {
  Dialog, DialogBackdrop, DialogPanel,
} from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  Dropdown, DropdownButton, DropdownItem, DropdownItems,
} from '@components/common';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import useWindowDimensions from '@hooks/useMediaQuery';


/** Types */
export interface CvActionMenuItem {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  /** Classes for the icon tile (e.g. status colour). */
  iconClassName?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  /** Marks the item as the current state (check mark). Still clickable when it has `onClick` (e.g. to unselect it). */
  current?: boolean;
  /** Red notification dot next to the label (e.g. unread messages). */
  hasDot?: boolean;
  separatorBefore?: boolean;
}

interface CvActionButtonProps {
  variant: 'soft' | 'primary';
  /** Label of the main part, or of the whole button when there is no main action. */
  label: ReactNode;
  /** Small line above the label in the main part (e.g. "Move to"). */
  eyebrow?: ReactNode;
  /** Small line under the label in the main part (e.g. call price). */
  subLabel?: ReactNode;
  icon?: ReactNode;
  badge?: ReactNode;
  /** Default action. When missing, the whole button opens the menu. */
  onMainClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  mainDisabled?: boolean;
  /** Extra classes for the main part (e.g. text colour of the next status). */
  mainClassName?: string;
  /** Hide the main label (keep icon + arrow) when the main part is too narrow to show it, instead of clipping it. */
  collapseLabel?: boolean;
  isLoading?: boolean;
  /** Accessible label for the arrow part. */
  menuLabel: string;
  menuHeader?: ReactNode;
  items: CvActionMenuItem[];
  /** Small uppercase caption above the button (desktop only). */
  caption?: ReactNode;
  className?: string;
}


// Classes
// *************************************
const variantClasses = {
  soft: {
    container: 'border border-surface-300 bg-surface-200 text-ink',
    part: 'hover:bg-surface-300',
    // Split arrow: one shade darker + divider, so it reads as its own control (also without hover, on mobile)
    arrow: 'border-l border-ink-light/40 bg-surface-300 hover:brightness-95',
  },
  primary: {
    container: 'border border-secondary bg-secondary text-white',
    part: 'hover:bg-secondary-dark',
    arrow: 'border-l border-white/35 bg-secondary-dark hover:brightness-95',
  },
};

const partBaseClasses = 'flex h-full min-w-0 cursor-pointer items-center gap-2 whitespace-nowrap font-semibold transition-colors duration-150 '
  + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-default [&>svg]:size-4.5 [&>svg]:shrink-0';

const smallLineClasses = 'truncate text-[11px] font-medium';


/**
 * @description One menu row; shared by the desktop dropdown and the mobile sheet.
 */
const MenuItemContent = ({ item }: { item: CvActionMenuItem }) => (
  <>
    {item.icon && (
      <span
        className={clsx(
          'flex size-8 shrink-0 items-center justify-center rounded-lg [&>svg]:size-4.5',
          item.tone === 'danger' ? 'bg-error/10 text-error' : 'bg-surface-100 text-primary',
          item.iconClassName,
        )}
      >
        {item.icon}
      </span>
    )}
    <span className="flex min-w-0 flex-col">
      <span className="flex items-center gap-2 font-medium">
        {item.label}
        {item.hasDot && <span className="block size-2 shrink-0 rounded-full bg-error" />}
      </span>
      {item.description && <span className="text-xs font-normal text-ink-medium">{item.description}</span>}
    </span>
    {item.current && <CheckIcon className="ml-auto size-4 shrink-0 text-ink-light" />}
  </>
);

const menuItemClasses = (item: CvActionMenuItem) => clsx(
  'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm',
  item.tone === 'danger' ? 'text-error' : 'text-ink',
  item.current && 'bg-surface-50',
  item.disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer hover:bg-surface-50 data-[focus]:bg-surface-50',
);


/**
 * @description Bottom sheet used on mobile instead of the dropdown. Rendered above the CV detail modal (z-[70]).
 */
const CvActionSheet = ({
  isOpen, onClose, header, items,
}: { isOpen: boolean; onClose: () => void; header?: ReactNode; items: CvActionMenuItem[] }) => (
  <Dialog open={isOpen} onClose={onClose} className="relative z-[80]">
    <DialogBackdrop transition className="fixed inset-0 bg-ink/40 transition-opacity duration-200 data-[closed]:opacity-0" />
    <div className="fixed inset-x-0 bottom-0">
      <DialogPanel
        transition
        className={clsx(
          'rounded-t-3xl bg-surface px-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-2.5 shadow-xl',
          'transition duration-300 ease-out data-[closed]:translate-y-full',
        )}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-surface-200" />
        {header && <div className="mb-1.5 border-b border-surface-100 px-2.5 pb-2.5 text-sm">{header}</div>}
        {items.map((item) => (
          <Fragment key={item.id}>
            {item.separatorBefore && <div className="mx-1 my-1.5 h-px bg-surface-100" />}
            <button
              type="button"
              disabled={item.disabled || (item.current && !item.onClick)}
              className={menuItemClasses(item)}
              onClick={(event) => {
                onClose();
                item.onClick?.(event);
              }}
            >
              <MenuItemContent item={item} />
            </button>
          </Fragment>
        ))}
      </DialogPanel>
    </div>
  </Dialog>
);


/**
 * @description CV detail action button. Split button when `onMainClick` is set (main part runs the default action,
 * the arrow opens the menu), otherwise the whole button opens the menu. The menu is a dropdown on desktop and a
 * bottom sheet on mobile.
 */
export const CvActionButton = (props: CvActionButtonProps) => {
  const {
    variant, label, eyebrow, subLabel, icon, badge, onMainClick, mainDisabled, mainClassName, collapseLabel, isLoading,
    menuLabel, menuHeader, items, caption, className,
  } = props;

  const { isMobile } = useWindowDimensions();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const styles = variantClasses[variant];
  const isSplit = !!onMainClick;

  const mainPart = isSplit && (
    <button
      type="button"
      onClick={onMainClick}
      disabled={mainDisabled || isLoading}
      // Keeps an accessible name when the collapsed label is hidden
      aria-label={collapseLabel && typeof label === 'string' ? label : undefined}
      className={clsx(
        partBaseClasses,
        styles.part,
        'grow justify-center rounded-l-[11px] pl-3.5 pr-2 disabled:opacity-70',
        // Mobile only: a size container ignores its content width, which would shrink the desktop (content-sized) button
        collapseLabel && 'max-md:@container',
        mainClassName,
      )}
    >
      {isLoading ? <TailwindSpinner className="!mx-0 size-4" /> : icon}
      <span className={clsx('min-w-0 flex-col items-start leading-tight', collapseLabel ? 'hidden @[7rem]:flex md:flex' : 'flex')}>
        {eyebrow && <span className={clsx(smallLineClasses, variant === 'primary' ? 'text-white/80' : 'text-ink-medium')}>{eyebrow}</span>}
        <span className="max-w-full truncate">{label}</span>
        {subLabel && <span className={clsx(smallLineClasses, variant === 'primary' ? 'text-white/80' : 'text-ink-medium')}>{subLabel}</span>}
      </span>
      {badge && (
        <span
          className={clsx(
            'hidden shrink-0 rounded-full px-2 py-0.5 text-tiny font-semibold md:inline-block',
            variant === 'primary' ? 'bg-white/25' : 'bg-surface-100 text-ink-medium',
          )}
        >
          {badge}
        </span>
      )}
    </button>
  );

  const triggerContent = isSplit ? <ChevronDownIcon className="size-5" /> : (
    <>
      {icon}
      <span className="truncate">{label}</span>
    </>
  );
  const triggerClasses = clsx(
    partBaseClasses,
    isSplit ? styles.arrow : styles.part,
    isSplit
      // Wider arrow (min 2.5rem on mobile, 30% of the button from md), so the dropdown stands out
      ? 'min-w-10 justify-center rounded-r-[11px] px-2'
      : 'grow justify-center rounded-[11px] px-3.5',
  );

  const containerClasses = clsx(
    'relative flex h-12 shrink-0 rounded-xl text-sm',
    // Split button from md: arrow part takes 30% of the width. Mobile keeps the flex row, a 30% arrow would make the
    // icon-only buttons too wide for the row.
    isSplit && 'md:grid md:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]',
    styles.container,
    className,
  );

  const captionElement = caption && (
    <span
      className={clsx(
        'absolute -top-6 left-0.5 hidden whitespace-nowrap',
        'text-[11px] font-semibold uppercase tracking-wider text-ink-light md:block',
      )}
    >
      {caption}
    </span>
  );

  // Mobile: arrow opens the bottom sheet
  // *************************************
  if (isMobile) {
    return (
      <div className={containerClasses}>
        {mainPart}
        <button
          type="button"
          aria-label={menuLabel}
          disabled={isLoading}
          className={triggerClasses}
          onClick={() => setIsSheetOpen(true)}
        >
          {triggerContent}
        </button>
        <CvActionSheet isOpen={isSheetOpen} onClose={() => setIsSheetOpen(false)} header={menuHeader} items={items} />
      </div>
    );
  }

  // Desktop: arrow opens the dropdown
  // *************************************
  return (
    <Dropdown className={containerClasses}>
      {captionElement}
      {mainPart}
      <DropdownButton type="button" aria-label={menuLabel} disabled={isLoading} className={triggerClasses}>
        {triggerContent}
      </DropdownButton>
      <DropdownItems anchor="bottom end" animation="slideDown" className="z-[100] mt-2 w-72 !rounded-2xl p-2">
        {menuHeader && <div className="mb-1.5 border-b border-surface-100 px-2.5 pb-2.5 pt-1 text-sm">{menuHeader}</div>}
        {items.map((item) => (
          <Fragment key={item.id}>
            {item.separatorBefore && <div className="mx-1 my-1.5 h-px bg-surface-100" />}
            <DropdownItem
              as="button"
              type="button"
              disabled={item.disabled || (item.current && !item.onClick)}
              className={menuItemClasses(item)}
              onClick={item.onClick}
            >
              <MenuItemContent item={item} />
            </DropdownItem>
          </Fragment>
        ))}
      </DropdownItems>
    </Dropdown>
  );
};
